import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Guard to protect routes that require authentication.
 * 
 * Checks if user is authenticated by verifying:
 * - User has cached Init API data (from AuthService)
 * - User is logged in (has valid session)
 * 
 * Usage:
 * { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard] }
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkAuth(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkAuth(state.url);
  }

  /**
   * Check if user is authenticated
   */
  private checkAuth(url: string): Observable<boolean> {
    console.log('[AuthGuard] Checking authentication for:', url);
    
    // Check if user is authenticated (has cached Init API data)
    if (this.authService.isAuthenticated()) {
      console.log('[AuthGuard] User is authenticated, allowing access');
      // User is authenticated, allow access
      return of(true);
    }

    // Not authenticated - try to initialize from cache or redirect to login
    const initData = this.authService.initDataValue;
    if (initData) {
      console.log('[AuthGuard] Found cached init data, user is authenticated');
      // We have cached data, user is authenticated
      return of(true);
    }

    // Check if we have valid tokens (even if initData is missing, which happens in new tabs)
    // If so, wait for init() to complete or allow access if tokens are valid
    const accessTokenExpiresAt = this.authService.getAccessTokenExpiresAt();
    const refreshTokenExpiresAt = this.authService.getRefreshTokenExpiresAt();
    const now = Date.now();
    
    if (accessTokenExpiresAt && refreshTokenExpiresAt) {
      const accessTokenExpired = accessTokenExpiresAt.getTime() <= now;
      const refreshTokenValid = refreshTokenExpiresAt.getTime() > now;
      
      console.log('[AuthGuard] Token expiration check:', {
        accessTokenExpired,
        refreshTokenValid,
        accessTokenExpiresAt: accessTokenExpiresAt.toISOString(),
        refreshTokenExpiresAt: refreshTokenExpiresAt.toISOString(),
      });
      
      if (refreshTokenValid) {
        // We have valid tokens but initData is missing (new tab scenario)
        // Trigger init() to restore state, then wait for it to complete
        console.log('[AuthGuard] Valid refresh token but initData missing (new tab) - triggering init()...');
        
        // Check if initData$ will emit soon (already in progress)
        const currentInitData = this.authService.initDataValue;
        if (currentInitData) {
          // initData is already available (race condition - it was set between checks)
          console.log('[AuthGuard] ✅ initData became available, allowing access');
          return of(true);
        }
        
        // Trigger init() and wait for it to complete
        return this.authService.init().pipe(
          map((initResponse) => {
            console.log('[AuthGuard] ✅ init() completed, allowing access');
            return true;
          }),
          catchError((error) => {
            console.error('[AuthGuard] ❌ init() failed:', error);
            // A terminal failure has already been handled by the interceptor (the terminator ran and
            // navigated). A temporary one leaves authentication unresolved, and the user stays where
            // they are while the shell retries.
            if (this.authService.authState !== 'unresolved') {
              this.router.navigate(['/auth/login'], {
                queryParams: { returnUrl: url },
              });
            }
            return of(false);
          })
        );
      }
    }

    // We had a session and could not confirm it — the server was unreachable or answered 5xx.
    // Refuse the route, but do not send the user to the login page: the shell is showing a retry
    // state, and bouncing to Login here is what turned a brief outage into a logout.
    if (this.authService.authState === 'unresolved') {
      console.log('[AuthGuard] Authentication unresolved; holding without redirecting');
      return of(false);
    }

    // No cached data and tokens are expired - redirect to login
    console.log('[AuthGuard] Not authenticated, redirecting to login');
    this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: url },
    });
    return of(false);
  }
}
